/**
 * Utility functions for Intercom SDK that don't require Vue instance context
 * Single source of truth for all Intercom SDK calls
 * Safe to call outside of Vue component context
 */

import Intercom, {
  boot as bootIntercomSDK,
  shutdown as shutdownIntercomSDK,
  update as updateIntercomSDK
} from '@intercom/messenger-js-sdk';

import type { IntercomUpdateData, IntercomUserInfo } from '@/types/intercom';

const { VITE_INTERCOM_APP_ID, VITE_VERSION } = import.meta.env;

/**
 * Expected shape of the user profile for Intercom syncing.
 */
interface IntercomUserProfile {
  email: string;
  firstName?: string;
  lastName?: string;
  permissions?: Array<{
    role: { name: string };
    scope: string;
    scopeDetails?: { name: string };
  }>;
  tenants?: Array<{ name: string }>;
}

// Brand primary — the widget's default accent at boot, until the live Vuetify
// theme is pushed via updateIntercomUserData. Exported so useIntercom's theme
// computed uses the same single source for its fallback.
export const DEFAULT_INTERCOM_COLOR = '#1A31D5';

// Whether the messenger currently has a live (non-shut-down) session. Cleared
// by clearIntercomUser() so the next initIntercom() re-boots the session.
let isIntercomBooted = false;

// Settings for every boot — the first-time Intercom() call and every
// post-shutdown boot() re-boot. Colors are brand defaults; the live Vuetify
// theme is pushed afterwards via updateIntercomUserData (updateIntercomTheme).
const bootSettings = {
  app_id: VITE_INTERCOM_APP_ID,
  action_color: DEFAULT_INTERCOM_COLOR,
  background_color: DEFAULT_INTERCOM_COLOR,
  theme_mode: 'light' as const
};

/**
 * Returns true when Intercom is configured for this build/environment.
 * Used to skip SDK calls on builds where VITE_INTERCOM_APP_ID is not set.
 */
export const isIntercomEnabled = (): boolean => !!VITE_INTERCOM_APP_ID;

/**
 * Boot the Intercom messenger with brand-default colors. Idempotent —
 * subsequent calls no-op until clearIntercomUser() shuts the session down.
 *
 * The SDK's Intercom() only performs a real boot on its first call, when it
 * injects the widget script. Once window.Intercom exists it degrades to
 * reattach_activator + update, which a shut-down messenger ignores — so a
 * re-boot after clearIntercomUser() (login-page reset, logout→re-login) has to
 * go through the SDK's boot(). Booting before any auth flow runs is what makes
 * the identity sync in useAuth's initApp() reliable.
 *
 * Colors are intentionally not parameterised: theming is owned by
 * updateIntercomUserData (see useIntercom.updateIntercomTheme), which App.vue
 * applies with the live Vuetify theme right after mount.
 */
export const initIntercom = (): void => {
  if (!isIntercomEnabled() || isIntercomBooted) return;

  try {
    // window.Intercom exists once the widget script is injected. Re-boot a
    // shut-down messenger through boot(); do the first-time inject-and-boot
    // through Intercom().
    if (window.Intercom) {
      bootIntercomSDK(bootSettings);
    } else {
      Intercom(bootSettings);
    }

    isIntercomBooted = true;
  } catch (error) {
    // Intercom must never block the app from starting.
    console.warn('Intercom init failed; continuing without Intercom.', error);
    isIntercomBooted = false;
  }
};

/**
 * Update Intercom with user information or theme configuration.
 * No-ops when Intercom is not configured (e.g. preview / CloudFront builds)
 * or has not been successfully booted.
 */
export const updateIntercomUserData = (data: IntercomUpdateData): void => {
  if (!isIntercomEnabled() || !isIntercomBooted) return;

  try {
    // We strip undefined values before passing to Intercom because the SDK
    // types use optional-but-not-undefined properties (exactOptionalPropertyTypes
    // compatibility), while our internal types allow undefined for safety.
    const cleanData = Object.fromEntries(
      Object.entries(data).filter(([_, v]) => v !== undefined)
    );
    updateIntercomSDK(cleanData);
  } catch (error) {
    console.warn('Intercom update failed.', error);
  }
};

/**
 * Sync user profile with Intercom on every environment.
 * Extracts email, name, roles, tenants, and other user metadata.
 * Safe to call outside Vue context (no composables required).
 *
 * Identity is synced on all environments; workspace separation is handled by
 * VITE_INTERCOM_APP_ID (prod points at the prod workspace, non-prod at the
 * test workspace).
 */
export const syncIntercomUser = (
  userProfile: IntercomUserProfile,
  options?: { hasSingleContext?: boolean }
): void => {
  if (!userProfile || !isIntercomEnabled()) return;

  const {
    email,
    firstName,
    lastName,
    permissions = [],
    tenants = []
  } = userProfile;
  const hasSingleContext = options?.hasSingleContext ?? tenants.length === 1;

  const intercomInfo: IntercomUserInfo = {
    email: email || undefined,
    name: `${firstName ?? ''} ${lastName ?? ''}`.trim() || undefined,
    single_context: hasSingleContext ? 'true' : 'false',
    roles:
      permissions
        .map((p) => p.role?.name)
        .filter(Boolean)
        .join(',') || undefined,
    scope_names:
      permissions
        .map((s) => s.scopeDetails?.name)
        .filter(Boolean)
        .join(',') || undefined,
    tenants:
      tenants
        .map((t) => t.name)
        .filter(Boolean)
        .join(',') || undefined,
    scopes:
      permissions
        .map((s) => s.scope)
        .filter(Boolean)
        .join(',') || undefined,
    app_version: VITE_VERSION || undefined
  };

  updateIntercomUserData(intercomInfo);
};

/**
 * Ends the Intercom user session on logout.
 * shutdown() is used over update()-with-empty-strings to avoid a 400 from Intercom.
 * No-ops if we never booted (which also implies Intercom is disabled, since the
 * boot flag is only set after a successful init), and resets the flag so the
 * next initIntercom() call starts a fresh (anonymous) session.
 */
export const clearIntercomUser = (): void => {
  if (!isIntercomBooted) return;

  try {
    shutdownIntercomSDK();
  } catch (error) {
    console.warn('Intercom shutdown failed.', error);
  } finally {
    isIntercomBooted = false;
  }
};
